import { CallPatientButton } from '@montugroup/clinic-telecom';
import Close from '@mui/icons-material/Close';
import { Box, IconButton, styled } from '@mui/material';

import { ConsultDetailInfo } from '@/components/Patient/ScheduledConsult/ConsultHistory/ConsultDetailInfo';
import { FF_C4C_ENABLE_CONNECT_VIDEO_CONSULT, FF_C4C_VIDEO_CONSULT } from '@/constants/featureFlags';
import { useFeatureFlags } from '@/hooks';
import type { Consultation } from '@/hooks/patients/usePatientConsultations';
import { useSavePatientTelecomAttributes } from '@/hooks/telecom/useSavePatientTelecomAttributes';
import { SectionHeader } from '@/shared-ui/PageElements/SectionHeader';
import type { ConsultationModel } from '@/types';
import { Logger } from '@/utils/logger';
import { triggerSourcePatientTelecomAttributeEnum } from '@montugroup/pms-api-contracts';

const logger = new Logger('ConsultationDetails');

const Heading = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
  marginBottom: theme.spacing(6),
}));

const DetailContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(6),
  flexDirection: 'column',

  [theme.breakpoints.up('xl')]: {
    flexDirection: 'row',
  },
}));

const CallContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flex: 1,
  flexDirection: 'column',
  maxWidth: '19rem',
  alignSelf: 'flex-start',

  [theme.breakpoints.up('xl')]: {
    alignSelf: 'flex-end',
  },
}));

export type ConsultationDetailsProps = {
  consultation: ConsultationModel;
  patientPhoneNumber?: string;
  patientId?: string;
  onClose?: (e?: React.MouseEvent<HTMLButtonElement>) => void;
  patientName: string;
};

export const ConsultationDetails = ({
  consultation,
  onClose,
  patientId,
  patientPhoneNumber,
  patientName,
}: ConsultationDetailsProps) => {
  const { flags } = useFeatureFlags();

  const isVideoConsult = flags[FF_C4C_VIDEO_CONSULT];
  const isVideoConsultEnabled = flags[FF_C4C_ENABLE_CONNECT_VIDEO_CONSULT];
  const displayVideoCallButton = isVideoConsult && isVideoConsultEnabled;

  const { mutateAsync: savePatientTelecomAttributes } = useSavePatientTelecomAttributes(
    Number(patientId),
    consultation.id,
  );

  const handleContact = async (contact: connect.Contact) => {
    try {
      await savePatientTelecomAttributes(
        contact.contactId,
        undefined,
        triggerSourcePatientTelecomAttributeEnum.enum.consultationVideoCallAttributes,
      );
    } catch (error) {
      logger.error('Failed to save patient telecom attributes', error);
    }
  };

  return (
    <>
      <Heading>
        <SectionHeader headerText="Consultation Details" />
        <IconButton onClick={onClose} size="small" data-testid="close-consultation-button">
          <Close />
        </IconButton>
      </Heading>

      <DetailContainer>
        <Box
          sx={(theme) => ({
            marginBottom: !displayVideoCallButton ? theme.spacing(6) : 0,
            marginRight: 0,

            [theme.breakpoints.up('xl')]: {
              marginBottom: 0,
              marginRight: !displayVideoCallButton ? theme.spacing(4) : 0,
            },
          })}
        >
          <ConsultDetailInfo consultation={consultation as unknown as Consultation} />
        </Box>

        {!displayVideoCallButton && (
          <CallContainer>
            <CallPatientButton
              patientId={patientId}
              patientPhoneNumber={patientPhoneNumber}
              onContact={handleContact}
              patientName={patientName}
            >
              Start Consultation
            </CallPatientButton>
          </CallContainer>
        )}
      </DetailContainer>
    </>
  );
};
